import {
  EnqueteCollectCounts,
  AgesGenderKeyObj,
  QualificationAge,
  DividedAges,
  AgesGenderKeyObjAccessor,
  SpePaneAccessor,
} from '@mytypes';
import { ADMIN_PATH_INDICATOR } from '~/constants/routes';
import {
  SPECIAL_PANEL_CATEGORIES,
  QUALIFICATION_GENDER_TYPE_NAMES,
  AGE_DIVIDE_TYPES,
  SPEPANE_AMSWER_LIST,
  SURVEY_STATUSES,
  INT_MAX_VALUE,
} from '~/constants';
import { validateUtf8mb4 } from '~/constants/validationRules';

/**
 * URLパスから、admin画面かどうか判定。
 *
 * @returns {Bool}
 */
export const isAdminPath = () => window.location.pathname.split('/')[1] === ADMIN_PATH_INDICATOR;

/**
 * URLパスから、パンくずを表示する画面かどうか判定。
 *
 * @returns {Bool}
 */
export const isDisplayPageLinkBar = () => {
  if (window.location.pathname.match(/^\/survey\/\d+$/)) {
    return true;
  }
  if (window.location.pathname.match(/^\/admin\/survey\/\d+$/)) {
    return true;
  }
  return false;
};

/**
 * URLパスから、ログイン画面かどうか判定。
 *
 * @returns {Bool}
 */
export const isSigninPath = () => {
  if (window.location.pathname.match(/^.*\/signin$/)) {
    return true;
  }
  return false;
};

/**
 * 第一引数のオブジェクト配列を第二引数のキーの値によってソート。
 *
 * @param {Array} objArray
 * @param {*} key
 * @param {*} orderBy
 * @returns
 */
export const sortObjArray = (
  objArray: AgesGenderKeyObj[],
  key: string,
  orderBy = 'asc'
): AgesGenderKeyObj[] => {
  objArray.sort((a: AgesGenderKeyObjAccessor, b: AgesGenderKeyObjAccessor): number => {
    if (orderBy === 'asc') {
      return a[key] < b[key] ? -1 : 1;
    }
    return a[key] > b[key] ? -1 : 1;
  });
  return objArray;
};

/**
 * 配列かオブジェクトの値を合計して返却。
 *
 * @param {Object | Array} items ソート対象のオブジェクト、もしくは配列。
 * @param {Bool} isObject 第一引数がオブジェクトの場合はtrue、配列の場合はfalseを指定してください。
 */
export const culcTotal = ({ items, isObject = true }: any) => {
  if (isObject) {
    return Object.keys(items).reduce((sum, key) => sum + Number(items[key]), 0);
  }

  return items.reduce((sum: number, item: number) => sum + item, 0);
};

/**
 * PropTypesでnull許容したいとき、このfunctionでラップする。
 * ex) propTypesNullable(PropTypes.string.isRequired)
 */
export const propTypesNullable =
  (propType: any) =>
  (props: { [x: string]: null }, propName: string | number, ...rest: any) => {
    if (props[propName] === null) {
      return null;
    }
    return propType(props, propName, ...rest);
  };

/**
 * iso 8601形式の日付Stringから、
 * 年, 月, 日, 曜日, 時間, 分を抽出して返却。
 *
 * @param {String} isoDate
 * @returns
 */
export function extractDateFromIsoDate(isoDate: string | null | undefined) {
  const [, year, month, dt, hour, minute] =
    isoDate?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/) || [];

  const weekday = new Date(`${year}-${month}-${dt}`).toLocaleDateString('ja', {
    weekday: 'short',
  });

  return {
    year: Number(year),
    month,
    dt,
    weekday,
    hour,
    minute,
  };
}

/**
 *
 * iso 8601形式の日付をxx日間を加算
 *
 * @param {String} isoDate
 * @param {Number} daysToAdd
 * @returns
 */
export function addDaystoIsoDate(isoDate: string, daysToAdd: number) {
  const date = new Date(isoDate);
  date.setDate(date.getDate() + daysToAdd);
  return date;
}

/**
 * ageFrom, ageTo, gender全てをキーに含むObjectから以下を作成。
 *
 * 1. 年齢のfrom-toを小さい順に並べた配列。
 * [`${ageFrom}-${ageTo}`, `${ageFrom}-${ageTo}`, ...]
 *
 * 2. `${ageFrom}-${ageTo}-${gender}`をkey、引数のobjをvalueとするObject。
 * {
 *   `${ageFrom}-${ageTo}-${gender}`: obj,
 *   `${ageFrom}-${ageTo}-${gender}`: obj,
 *   ...
 * }
 *
 * ageFrom, ageTo, genderどれかひとつでもキーに持たないObjectが渡された場合は、
 * 1も2も空配列で返却される。
 *
 * @param objArray ageFrom, ageTo, gender全てをキーに含むObjectの配列
 * @param key 2のobjに当たる部分が、obj[key]になる（keyを渡さない、もしくはobj[key]がundefinedの場合はobjになる）
 * @returns
 */
export const createAgesGenderKeyObjs = (
  objArray: AgesGenderKeyObj[],
  key: string | null = null
) => {
  // 空オブジェクトが渡された場合は空配列と空objectを早期リターン。
  if (objArray.length === 0) {
    return [[], {}];
  }

  const ages: string[] = [];
  const entry: any[] = [];

  /**
   * quotasをageFromでソートしておいて、以下のような2つの配列を作成。
   * [`${ageFrom}-${ageTo}`, ...]
   * [[`${ageFrom}-${ageTo}-${gender}`, quota], ...]
   */

  sortObjArray([...objArray], 'ageFrom').forEach((item) => {
    if (!item.ageFrom || !item.ageTo || !item.gender) {
      return;
    }

    ages.push(`${item.ageFrom}-${item.ageTo}`);

    const val =
      key && (item as AgesGenderKeyObjAccessor)[key] !== undefined
        ? (item as AgesGenderKeyObjAccessor)[key]
        : item;
    entry.push([`${item.ageFrom}-${item.ageTo}-${item.gender}`, val]);
  });
  // agesの重複を排除したものとentryをobject化したものを返却
  return [Array.from(new Set(ages)), Object.fromEntries(entry)];
};

/**
 * スペパネ設問の選択状態オブジェクトから、選択されたものだけの配列を生成して返却する。
 *
 * @param {object} checkedItems スペパネ設問の選択状態オブジェクト
 * @param {bool} isDisplayName trueなら選択肢名の配列を返す。デフォルトはSegm用のqidの配1列を返す。
 * @returns
 */
export const convertSpecialPanelCheckedItemsToArray = (
  checkedItems: any,
  specialPanelDisplaySettingId: number,
  isDisplayName = false
) => {
  if (!checkedItems || !specialPanelDisplaySettingId) {
    return [];
  }
  return Object.keys(checkedItems)
    .map((key) => {
      if (
        key === 'isAllChecked' ||
        !checkedItems[key] ||
        !Object.keys(SPEPANE_AMSWER_LIST[specialPanelDisplaySettingId]).includes(key)
      ) {
        return false;
      }
      /* istanbul ignore next 下記if分以外をelseにてreturn falseにすればカバレッジは通るが、分かりにくくなるのでカバレッジから除外する */
      if (checkedItems[key]) {
        return isDisplayName
          ? (SPEPANE_AMSWER_LIST[specialPanelDisplaySettingId] as SpePaneAccessor)[key]
          : key;
      }
      /* istanbul ignore next 通過しないがlint上必要なのでカバレッジからは除外 */
      return false;
    })
    .filter(Boolean);
};

/**
 * スペパネのチェック状態を管理するObjectから、対象者数取得API（Segm）用に渡すパラメータ用配列を生成する。
 * spCheckedItemsは、qualificationsSliceのspecialConditions.checkedItemsのフォーマット。
 * categoryごとにチェック状態がtrueになっている選択肢idの配列を作成し、配列に詰める。
 *
 * specialConditionsArrayは以下のような二重配列になる。
 * [
 *   ['OR', choiceId, choiceId, ...],
 *   ['OR', choiceId, choiceId, ...],
 *   ...
 * ]
 *
 * 最後にそれを以下のような形式にして返却。
 * ['AND', ['OR', choiceId, choiceId, ...], [choiceId, choiceId, ...], ...]
 *
 * 全設問で一つも選択されていない時は空配列を返却。
 */
export const makeSpecialPanelConditions = (
  spCheckedItems: any,
  specialPanelDisplaySettingId: number
) => {
  const categories = SPECIAL_PANEL_CATEGORIES[specialPanelDisplaySettingId];
  const specialConditionsArray = categories
    .map((category) => {
      const specialConditionsArrayByCategory = convertSpecialPanelCheckedItemsToArray(
        spCheckedItems[category],
        specialPanelDisplaySettingId
      );

      // 一つも選択されていない時はfalseを返却
      if (specialConditionsArrayByCategory.length === 0) {
        return false;
      }
      return ['OR', ...specialConditionsArrayByCategory];
    })
    .filter(Boolean);

  // 全ての設問で一つも選択されていない時は空配列を返却
  if (specialConditionsArray.length === 0) {
    return [];
  }
  return ['AND', ...specialConditionsArray];
};

/**
 * 地域コードの配列を地域名に変換する。
 * @param {array[number]} AreaCds [1, 2, 3, ...]
 */
export const convertAreaCdsToDisplayNameArray = (areas: any[], areaCds: string[]): any[] => {
  const areaNames = areas
    .map((parent) => {
      if (!parent.children) {
        return false;
      }
      return parent.children
        .map((child: { areaCd: string; areaNm: string }) => {
          if (areaCds.includes(child.areaCd)) {
            return child.areaNm;
          }
          return false;
        })
        .filter(Boolean);
    })
    .filter(Boolean);
  return areaNames.flat();
};

/**
 * 性別コードを表示名に変換して、配列として返却する。
 *
 * @param {Number | null} gender 1:男性のみ, 2:女性のみ, 3:男女合わせる, 4: 男女それぞれ（分割）
 */
export const convertGenderToDisplayNameArray = (gender: number | null | undefined): string[] => {
  if (gender === null || gender === undefined) {
    return [];
  }
  return [QUALIFICATION_GENDER_TYPE_NAMES[gender]];
};

/**
 * qualificationSliceのageオブジェクトから、
 * divideType（0: 分割しない, 1: 5歳ごと, 2: 10歳ごと）に従って分割された状態の配列を生成して返却。
 *
 * @param age qualificationSliceのageオブジェクト。
 * @returns divideType（0: 分割しない, 1: 5歳ごと, 2: 10歳ごと）に従って分割された状態の配列。
 */
export const divideAges = (age: QualificationAge): DividedAges[] => {
  // どちらがnullなら空で返す
  if (age.from == null || age.to == null) {
    return [];
  }

  // 年代区切りしない場合はそのまま返す
  if (age.divideType === AGE_DIVIDE_TYPES.NONE) {
    return [{ from: age.from, to: age.to }];
  }

  // age.from age.to同じ場合、そのまま返す
  if (age.from === age.to) {
    return [{ from: age.from, to: age.to }];
  }

  // 5歳区切りか10歳区切りの場合
  const ages: DividedAges[] = [];
  const isStepTen = age.divideType === AGE_DIVIDE_TYPES.TEN;
  const step = isStepTen ? 10 : 5;
  let i;

  // 他の年代をまたがない場合はそのまま返す
  if (isStepTen) {
    // 十の位を比較して同一
    if (String(age.from).slice(0, 1) === String(age.to).slice(0, 1)) {
      return [{ from: age.from, to: age.to }];
    }
  }

  // 初回のfrom to値の調整 最初のto要素を条件によって丸める必要がある
  // from 一の位が0の時はそのままループに
  if (String(age.from).slice(-1) === '0') {
    i = age.from;
  } else {
    // それ以外 最初のto要素 一の位は4か9となる
    let to;
    if (isStepTen) {
      to = Math.ceil(age.from / 10) * 10 - 1;
    } else {
      // 5歳区切りの時の最初のto要素 一の位１〜４までは4、5~9までは9に丸める カバレッジを通したいのでlintを無効
      // eslint-disable-next-line no-lonely-if
      if (['1', '2', '3', '4'].includes(String(age.from).slice(-1))) {
        to = Math.ceil(age.from / 10) * 10 - 6;
      } else {
        to = Math.ceil(age.from / 10) * 10 - 1;
      }
    }

    // スタートはto + 1の数字
    i = to + 1;
    ages.push({
      from: age.from,
      to,
    });
  }

  for (i; i <= age.to; i += step) {
    const to = i + step - 1;
    // 次の区切り年齢がスライダーの上限値と同じか超えていたら端数をpushする
    if (to === age.to) {
      ages.push({
        from: to - step + 1,
        to: age.to,
      });
      break;
    } else if (to > age.to) {
      ages.push({
        from: i,
        to: age.to,
      });
      break;
    }

    ages.push({
      from: i,
      to: i + step - 1,
    });
  }
  return ages;
};

/**
 * qualificationSliceのageオブジェクトから、
 * divideType（0: 分割しない, 1: 5歳ごと, 2: 10歳ごと）に従って分割された状態の配列を生成して返却。
 *
 * @param age - qualificationSliceのageオブジェクト。
 * @returns divideType（0: 分割しない, 1: 5歳ごと, 2: 10歳ごと）に従って分割され、
 * ハイフンで繋がれた文字列(= `${from}-${to}`)の配列。
 */
export const convertAgeToDisplayNameArray = (age: QualificationAge): string[] => {
  const ages = divideAges(age);
  return ages.map((item) => `${item.from}-${item.to}`);
};

/**
 * アンケート詳細画面で編集可能なステータスかどうかをステータスコードから判定。
 * 下書き・却下の場合のみtrueを返却。
 *
 * @param {Number} surveyStatusId - 調査のステータスコード
 * @returns {Bool}
 */
export const canEditSurveyStatus = (surveyStatusId: number) =>
  (surveyStatusId !== null || surveyStatusId !== undefined) &&
  [SURVEY_STATUSES.REGISTERING, SURVEY_STATUSES.REJECTED].includes(surveyStatusId);

/**
 * enqueteCollectCountsからstatusCountsだけそれぞれ取り出して、全合計を返す。
 *
 * @param {Object} enqueteCollectCounts - 調査のステータスコード
 * @returns {Number}
 */
export const calcEnqueteCollectSum = (enqueteCollectCounts: EnqueteCollectCounts = {}) => {
  if (Object.keys(enqueteCollectCounts).length > 0) {
    const total = Object.values(enqueteCollectCounts).reduce((prev, curr) => prev + curr);
    return total;
  }
  return 0;
};

/**
 * 全角文字 => 半角文字にして返却する。半角文字はそのままで返却
 *
 * @param {String} str
 * @returns {String}
 */
export const replaceFullToHalf = (str: string) =>
  str.toString().replace(/[！-～]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));

/**
 * Intの範囲かを判定する
 * 1 ~ 2147483647(INT_MAX_VALUE)ならtrueを返す
 *
 * @param {Number} number
 * @returns {Bool}
 */
export const isIntRange = (number: number) => number > 0 && number <= INT_MAX_VALUE;

/**
 * 値が空のkeyを除外する
 * すべてのkeyが除外された場合はundefinedを返す
 *
 * @param {Object} params
 * @returns {Object | Undefined}
 */
export const emptyValueFilter = (params: any) => {
  const res = Object.keys(params)
    .filter((key) => params[key] !== '' && params[key] != null)
    .reduce((obj: { [key: string]: any }, key) => {
      // eslint-disable-next-line no-param-reassign
      obj[key] = params[key];
      return obj;
    }, {});
  return !Object.keys(res).length ? undefined : res;
};

export const removeTagsOnAnswerLabel = (answerLabel: string) => {
  let labelWithoutTags = answerLabel;

  // Remove img tag
  const imgTagRegex = /<img.*\/>/g;
  const newLabel = labelWithoutTags.match(imgTagRegex);
  if (newLabel !== null) {
    newLabel.forEach((imgTag) => {
      labelWithoutTags = labelWithoutTags.replace(imgTag, '');
    });
  }

  return labelWithoutTags;
};

export const removeTagsOnQuestionLabel = (questionLabel: string) => {
  let labelWithoutTags = questionLabel;

  // Remove iframe tag
  const iFrameTagRegex = /<iframe.*<\/iframe>/g;
  let newLabel = questionLabel.match(iFrameTagRegex);

  if (newLabel !== null) {
    labelWithoutTags = labelWithoutTags.replace(newLabel[0], '');
  }

  // Remove img tag
  const imgTagRegex = /<img.*\/>/g;
  newLabel = labelWithoutTags.match(imgTagRegex);
  if (newLabel !== null) {
    newLabel.forEach((imgTag) => {
      labelWithoutTags = labelWithoutTags.replace(imgTag, '');
    });
  }

  // Remove <b></b> tag and retain text inside
  const boldTagRegex = /<b.*<\/b>/g;
  newLabel = labelWithoutTags.match(boldTagRegex);
  if (newLabel !== null) {
    newLabel.forEach((boldTag) => {
      const span = document.createElement('span');
      span.innerHTML = boldTag;

      if (span.textContent) {
        labelWithoutTags = labelWithoutTags.replace(boldTag, span.textContent);
      }
    });
  }

  // Remove <i></i> tag and retain text inside
  const italicTag = /<i.*<\/i>/g;
  newLabel = labelWithoutTags.match(italicTag);
  if (newLabel !== null) {
    newLabel.forEach((italic) => {
      const span = document.createElement('span');
      span.innerHTML = italic;
      if (span.textContent) {
        labelWithoutTags = labelWithoutTags.replace(italic, span.textContent);
      }
    });
  }

  // Remove <u></u> tag and retain text inside
  const underlineTag = /<u.*<\/u>/g;
  newLabel = labelWithoutTags.match(underlineTag);
  if (newLabel !== null) {
    newLabel.forEach((underline) => {
      const span = document.createElement('span');
      span.innerHTML = underline;
      if (span.textContent) {
        labelWithoutTags = labelWithoutTags.replace(underline, span.textContent);
      }
    });
  }

  // Remove link tags
  const linkTagRegex = /<a.*<\/a>/g;
  newLabel = labelWithoutTags.match(linkTagRegex);
  if (newLabel !== null) {
    newLabel.forEach((linkTag) => {
      const span = document.createElement('span');
      span.innerHTML = linkTag;
      if (span.textContent) {
        labelWithoutTags = labelWithoutTags.replace(linkTag, span.textContent);
      }
    });
  }

  // Remove <br> tags
  const brTagRegex = /<br\s*\/?>/g;
  newLabel = labelWithoutTags.match(brTagRegex);
  if (newLabel !== null) {
    labelWithoutTags = labelWithoutTags.replace(brTagRegex, '');
  }

  return labelWithoutTags;
};

export const getOriginalFormsValidationMsg = (label: string, maxLength?: number) => {
  let validationMsg = '';
  if (!label || label.trim() === '') {
    validationMsg = 'registration.originalForm.manual.inputBox.validationError';
  } else if (!validateUtf8mb4(label)) {
    validationMsg = 'registration.originalForm.manual.inputBox.validationUtf8mb4Error';
  } else if (maxLength && label.length > maxLength) {
    validationMsg = 'registration.originalForm.manual.inputBox.validationMaxlengthError';
  } else {
    validationMsg = 'registration.originalForm.manual.inputBox.validationNgWordsError';
  }

  return validationMsg;
};

export const unescapeQuestionsContentLabel = (escapedText: string) => {
  if (escapedText === undefined || escapedText?.trim().length === 0) {
    return '';
  }

  let result: string = escapedText;

  result = result.replace(/&lt;/g, '<');
  result = result.replace(/&gt;/g, '>');
  result = result.replace(/&amp;/g, '&');

  return result;
};

export const escapeQuestionsContentLabel = (text: string) => {
  if (text === undefined || text?.trim().length === 0) {
    return '';
  }

  const pattern = /<(\/)?(b|i|u|a|img|iframe|br)(\b[^>]*>|>)/g;
  const tagsToKeep: { [key: string]: string } = {};

  let keyIndex = 0;
  let result = text.replace(pattern, (match) => {
    const placeholder = `saveTag${keyIndex}saveTag`;
    tagsToKeep[placeholder] = match;
    keyIndex += 1;
    return placeholder;
  });

  result = result.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  Object.keys(tagsToKeep).forEach((key) => {
    result = result.replace(key, tagsToKeep[key]);
  });

  return result;
};
